import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff, LogIn, AlertCircle, User, ShieldAlert } from "lucide-react";
import { syndicatAuthApi } from "../../services/api";
import logoHorizontale from "../../assets/logo/logo-horizontale.svg";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [autoLoggedOut, setAutoLoggedOut] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (sessionStorage.getItem("autoLogout")) {
      setAutoLoggedOut(true);
      sessionStorage.removeItem("autoLogout");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const data = await syndicatAuthApi.login(username, password);
      localStorage.setItem("token", data.accessToken);
      if (data.refreshToken) localStorage.setItem("refreshToken", data.refreshToken);
      localStorage.setItem("role", "syndicat");
      localStorage.setItem("userName", data.nom || username);
      localStorage.setItem("userId", data.id || "");
      localStorage.setItem("syndicatCode", data.code || "");
      localStorage.setItem("syndicatId", data.id || "");
      navigate("/syndicat");
    } catch (err) {
      setError(err.message || "Identifiants incorrects. Vérifiez votre username et mot de passe.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex font-[Inter,sans-serif]">
      {/* Panneau gauche */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-emerald-800 via-emerald-700 to-teal-600">
        {/* Motif hexagonal fond */}
        <div className="absolute inset-0 opacity-[0.07]">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hex" width="56" height="100" patternUnits="userSpaceOnUse">
                <polygon points="28,2 54,16 54,44 28,58 2,44 2,16" fill="none" stroke="white" strokeWidth="1" />
                <polygon points="28,52 54,66 54,94 28,108 2,94 2,66" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hex)" />
          </svg>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-2/5 bg-gradient-to-t from-emerald-900/70 to-transparent" />

        <div className="relative z-10 flex flex-col justify-between p-10 w-full h-full">
          {/* Badge discret en haut */}
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-300 rounded-full animate-pulse" />
            <span className="text-white/60 text-xs font-medium tracking-widest uppercase">Sénégal · 2026</span>
          </div>

          {/* Visualisation réseau de pharmacies */}
          <div className="flex-1 flex items-center justify-center">
            <svg viewBox="0 0 420 340" className="w-full h-full max-h-96" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                  <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
                </filter>
                <filter id="glowSm">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
                </filter>
              </defs>

              {/* Lignes de connexion réseau */}
              {[
                [210,170, 95,75],  [210,170, 105,215], [210,170, 185,295],
                [210,170, 315,75], [210,170, 330,215], [210,170, 320,295],
                [210,170, 48,155], [210,170, 372,155],
                [95,75,  105,215], [315,75, 330,215],
                [105,215,185,295], [330,215,320,295],
                [48,155, 95,75],   [372,155,315,75],
                [185,295,320,295],
              ].map(([x1,y1,x2,y2], i) => (
                <line key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke="white" strokeWidth="1" strokeOpacity="0.18" />
              ))}

              {/* Noeuds secondaires */}
              {[
                [95, 75,  "Saint-Louis",  "right"],
                [315,75,  "Louga",         "left"],
                [48, 155, "Dakar",         "right"],
                [372,155, "Tambacounda",   "left"],
                [105,215, "Thiès",         "right"],
                [330,215, "Kaolack",       "left"],
                [185,295, "Ziguinchor",    "right"],
                [320,295, "Kolda",         "left"],
              ].map(([cx, cy, label, side], i) => (
                <g key={i}>
                  <circle cx={cx} cy={cy} r="22" fill="white" fillOpacity="0.07" />
                  <circle cx={cx} cy={cy} r="14" fill="white" fillOpacity="0.13" filter="url(#glowSm)" />
                  <circle cx={cx} cy={cy} r="8"  fill="white" fillOpacity="0.92" />
                  <circle cx={cx} cy={cy} r="3.5" fill="#34D399" />
                  <text
                    x={side === "right" ? cx + 15 : cx - 15}
                    y={cy + 4}
                    fill="white"
                    fontSize="10"
                    fontFamily="Inter,sans-serif"
                    fontWeight="600"
                    opacity="0.85"
                    textAnchor={side === "right" ? "start" : "end"}
                  >{label}</text>
                </g>
              ))}

              {/* Noeud central — hub national */}
              <circle cx="210" cy="170" r="56" fill="white" fillOpacity="0.05" />
              <circle cx="210" cy="170" r="40" fill="white" fillOpacity="0.08" />
              <circle cx="210" cy="170" r="28" fill="white" fillOpacity="0.18" filter="url(#glow)" />
              <circle cx="210" cy="170" r="20" fill="white" fillOpacity="0.95" />
              {/* Croix pharmacie centrale */}
              <rect x="198" y="167" width="24" height="6" rx="2" fill="#059669" />
              <rect x="207" y="158" width="6" height="24" rx="2" fill="#059669" />

              {/* Labels hub */}
              <text x="210" y="212" fill="white" fontSize="11" fontFamily="Inter,sans-serif"
                fontWeight="700" textAnchor="middle" opacity="0.95">Réseau National</text>
              <text x="210" y="227" fill="white" fontSize="9" fontFamily="Inter,sans-serif"
                opacity="0.5" textAnchor="middle">8 syndicats · 500+ pharmacies</text>

              {/* Anneau pulse */}
              <circle cx="210" cy="170" r="48" stroke="white" strokeWidth="1.5" strokeOpacity="0.0">
                <animate attributeName="r" values="46;65;46" dur="3s" repeatCount="indefinite" />
                <animate attributeName="stroke-opacity" values="0.25;0;0.25" dur="3s" repeatCount="indefinite" />
              </circle>
            </svg>
          </div>

          {/* Footer texte */}
          <div className="text-white">
            <h2 className="text-3xl font-bold mb-3 leading-tight">
              Votre réseau<br />pharmaceutique<br />au Sénégal
            </h2>
            <p className="text-white/65 text-sm leading-relaxed">
              Plannings de garde · Réseau de pharmacies · Syndicats
            </p>
            <div className="mt-5 flex items-center gap-2 text-xs text-white/40">
              <div className="w-1.5 h-1.5 bg-emerald-300 rounded-full animate-pulse" />
              <span>Plateforme sécurisée</span>
              {/* <span>Plateforme sécurisée · Données hébergées en France</span> */}
            </div>
          </div>
        </div>
      </div>

      {/* Panneau droit — Formulaire */}
      <div className="flex-1 flex flex-col justify-center items-center bg-gray-50 p-5 sm:p-8">
        <div className="w-full max-w-md">
          {/* Logo + titre */}
          <div className="mb-8">
            <img src={logoHorizontale} alt="SunuFarmasi" className="h-16 w-auto mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Connexion Secteur</h1>
            <p className="text-gray-400 text-sm">
              Accédez à votre espace de gestion du réseau pharmaceutique.
            </p>
          </div>

          {/* Badge Sénégal */}
          <div className="flex items-center space-x-2 mb-6 px-4 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl">
            <span className="text-lg">🇸🇳</span>
            <span className="text-emerald-800 text-sm font-medium">Plateforme nationale des pharmacies du Sénégal</span>
          </div>

          {/* Message déconnexion automatique */}
          {autoLoggedOut && (
            <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start space-x-3">
              <ShieldAlert className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
              <p className="text-amber-800 text-sm">
                Pour des raisons de sécurité, vous avez été déconnecté automatiquement après 5 minutes d'inactivité.
              </p>
            </div>
          )}

          {/* Message d'erreur */}
          {error && (
            <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={18} />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Identifiant syndicat
              </label>
              <div className="relative">
                <User size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="ex: syndicat.dakar"
                  required
                  className="w-full pl-11 pr-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 transition-all placeholder:text-gray-300"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Mot de passe
              </label>
              <div className="relative">
                <Lock size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full pl-11 pr-12 py-3.5 bg-white border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 transition-all placeholder:text-gray-300"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-all disabled:opacity-60 flex items-center justify-center space-x-2 shadow-lg shadow-emerald-200 hover:shadow-emerald-300 mt-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Connexion en cours...</span>
                </>
              ) : (
                <>
                  <LogIn size={17} />
                  <span>Se connecter</span>
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center text-xs text-gray-400">
            <p>Identifiants fournis par votre administrateur.</p>
            <p className="mt-1">
              © 2026 SunuFarmasi · <a href="https://sunufarmasi.app" target="_blank"><span className="text-emerald-600">sunufarmasi.app</span></a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
